import type { RenderComponentProps } from "replo-runtime/shared/types";

import React from "react";

import { ReploComponent } from "replo-runtime/store/components/ReploComponent";
import { useCurrentMediaSize } from "replo-runtime/store/hooks/useCurrentMediaSize";

import { useBundleBuilder } from "../BundleBuilderContext";

export const BundleGrid: React.FC<RenderComponentProps> = ({
  context,
  component,
  componentAttributes,
}) => {
  const { bundleTargetType, bundleProductsWithVariants, getProductData } =
    useBundleBuilder();
  const mediaSize = useCurrentMediaSize();
  const numColumns = mediaSize === "lg" ? 3 : 2;

  const tiles: { productId: string; variantId?: string }[] =
    React.useMemo(() => {
      if (!bundleProductsWithVariants) {
        return [];
      }

      return bundleProductsWithVariants.flatMap((product) => {
        const { productId, variantIds } = product;
        if (bundleTargetType === "products") {
          return [{ productId }];
        }
        let variantIdsArray = variantIds ?? [];
        if (variantIdsArray.length === 0) {
          const productData = getProductData(productId);
          variantIdsArray =
            productData?.variants.map(({ id }) => String(id)) ?? [];
        }
        // If we're using variants, we need to check if a list is provided
        return variantIdsArray.map((variantId) => ({
          productId,
          variantId,
        }));
      });
    }, [bundleProductsWithVariants, bundleTargetType, getProductData]);
  const productTileComponent = component.children?.[0];
  return (
    <div
      {...componentAttributes}
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
        gap: "24px",
        marginLeft: "12px",
        marginRight: "12px",
        flexGrow: 1,
      }}
    >
      {productTileComponent &&
        tiles?.map(({ productId, variantId }) => (
          <ReploComponent
            key={variantId ?? productId}
            context={context}
            component={{
              ...productTileComponent,
              props: { ...productTileComponent.props, productId, variantId },
            }}
          />
        ))}
    </div>
  );
};
